<script setup lang="ts">
import { computed, ref } from 'vue'
import { FwbButton } from 'flowbite-vue'
import { storeToRefs } from 'pinia'
import Api from '@/api'
import { useAuthStore } from '@/stores/auth'
import { UserRole } from '@/misc/types'
import { useAdminStore } from '@/stores/admin'

const { user, isImpersonating } = storeToRefs(useAuthStore())
const { projects } = storeToRefs(useAdminStore())
const isProcessing = ref(false)

const stopImpersonating = async () => {
  if (isProcessing.value) return
  isProcessing.value = true
  try {
    await Api.stopImpersonation()
  } finally {
    isProcessing.value = false
  }
}

const roleLabel = computed(() => {
  if (typeof user.value.impersonatedRole !== 'number') return null
  return UserRole[user.value.impersonatedRole] as string
})

const projectNameMap = computed(() => {
  const map = new Map<number, string>()
  projects.value.forEach((p) => map.set(Number(p.value), p.name))
  return map
})

const projectSummaries = computed(() => {
  if (!Array.isArray(user.value.impersonatedProjectIds)) return []
  return user.value.impersonatedProjectIds.map((id) => {
    return projectNameMap.value.get(Number(id)) ?? `Project #${id}`
  })
})

const mode = computed(() => user.value.impersonationMode)

const buttonLabel = computed(() => {
  if (mode.value === 'access') return 'Exit access impersonation'
  return `Return to ${user.value.impersonatorUsername ?? 'original account'}`
})
</script>

<template>
  <div
    v-if="isImpersonating"
    class="absolute mt-2 left-1/2 transform -translate-x-1/2 z-30 w-1/2 bg-amber-100 dark:bg-amber-400/20 text-amber-900 dark:text-amber-100"
  >
    <div class="max-w-5xl mx-auto px-4 py-2 flex flex-col gap-2 sm:flex-row sm:items-center">
      <div class="text-sm">
        <strong>Impersonation active:</strong>
        <template v-if="mode === 'user'">
          You are acting as
          <span class="font-semibold">{{ user.username }}</span
          >. Permissions now mirror this account.
        </template>
        <template v-else>
          You are emulating a
          <span class="font-semibold">{{ roleLabel ?? 'limited' }}</span>
          user.
          <span v-if="projectSummaries.length">
            Projects limited to:
            <span class="font-semibold">{{ projectSummaries.join(', ') }}</span>
          </span>
          <span v-else>All project access is currently blocked.</span>
        </template>
      </div>
      <FwbButton
        size="xs"
        color="light"
        class="w-full sm:w-auto ms-auto"
        :disabled="isProcessing"
        @click="stopImpersonating"
      >
        {{ buttonLabel }}
      </FwbButton>
    </div>
  </div>
</template>
